'use client';

import { skipToken } from '@reduxjs/toolkit/query';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { appRoutes } from '@/appRoutes';
import { WorkspaceSwitcher } from '@/features/auth/components';
import { useGetChatDestinationsQuery } from '@/features/chat/api/chatApi';
import { ChatRealtimeProvider } from '@/features/chat/realtime/ChatRealtimeProvider';
import {
  ContextTabsController,
  ContextTabsProvider,
  useContextTabsContext,
} from '@/features/context-tabs';
import { getBillingAccessState } from '@/features/subscription/utils/billingAccess';
import { useGetCurrentUserContextQuery } from '@/features/users/api/usersApi';
import { buildSettingsSectionAccess } from '@/features/workspaces/selectors';
import { setCurrentWorkspace } from '@/features/workspaces/slices/workspacesSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { MobileNavDrawer, SideNav, Topbar, getWorkspaceNavItems } from '@/ui';
import { useIsMobile } from '@web/hooks';
import { Banner, Link } from '@web/ui';

import styles from './WorkspaceAppShell.module.scss';

type WorkspaceAppShellProps = {
  children: React.ReactNode;
  workspaceSlug: string;
};

function ContextTabRouteContent({ children }: { children: React.ReactNode }) {
  const contextTabs = useContextTabsContext();
  return (
    <Fragment key={contextTabs?.currentRouteTabId ?? 'context-tab-fallback'}>
      {children}
    </Fragment>
  );
}

export function WorkspaceAppShell({
  children,
  workspaceSlug,
}: WorkspaceAppShellProps) {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const termsAcceptanceRequired = useAppSelector(
    (state) => state.legal.termsAcceptanceRequired,
  );
  const currentWorkspace = useAppSelector(
    (state) => state.workspaces.currentWorkspace,
  );
  const {
    data: currentUserContextResponse,
    isLoading: isBillingAccessLoading,
  } = useGetCurrentUserContextQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const currentUserContextWorkspace =
    currentUserContextResponse?.data?.workspace;
  const currentUser = currentUserContextResponse?.data?.user;

  useEffect(() => {
    if (currentUserContextWorkspace !== undefined) {
      dispatch(setCurrentWorkspace(currentUserContextWorkspace));
    }
  }, [currentUserContextWorkspace, dispatch]);

  const billingAccessState = getBillingAccessState(
    currentUserContextWorkspace?.plan,
  );
  const hasPremiumAccess =
    isBillingAccessLoading ||
    billingAccessState === 'premium_active' ||
    billingAccessState === 'trial_active';
  const isChatRealtimeEnabled = !isBillingAccessLoading && hasPremiumAccess;

  const settingsAccess = useMemo(
    () => buildSettingsSectionAccess(currentWorkspace),
    [currentWorkspace],
  );
  const chatDestinationsQuery = useGetChatDestinationsQuery(
    isChatRealtimeEnabled ? { page_size: 100 } : skipToken,
  );
  const hasUnreadChat = useMemo(
    () =>
      (chatDestinationsQuery.data?.data?.results ?? []).some(
        (destination) => (destination.unread_count ?? 0) > 0,
      ),
    [chatDestinationsQuery.data],
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = useMemo(
    () =>
      getWorkspaceNavItems(workspaceSlug, {
        settingsAccess,
        hasPremiumAccess,
        hasUnreadChat,
      }),
    [hasPremiumAccess, hasUnreadChat, settingsAccess, workspaceSlug],
  );
  const termsSettingsHref =
    appRoutes.workspace.settings(workspaceSlug).termsAndConditions.index;

  const shell = (
    <ChatRealtimeProvider enabled={isChatRealtimeEnabled}>
      <div className={styles.shell}>
        <Topbar
          workspaceSlug={workspaceSlug}
          onOpenMenu={() => setDrawerOpen(true)}
          hasUnreadChat={hasUnreadChat}
          contextTabs={<ContextTabsController />}
        />

        {!isMobile && (
          <SideNav
            items={items}
            footerTopContent={({ isExpanded }) => (
              <div className={styles.footerUtilities}>
                <WorkspaceSwitcher isSideNavExpanded={isExpanded} />
              </div>
            )}
          />
        )}

        {isMobile && (
          <MobileNavDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            items={items}
            footerTopContent={() => (
              <div className={styles.footerUtilities}>
                <WorkspaceSwitcher isSideNavExpanded speedDialDirection="up" />
              </div>
            )}
          />
        )}

        <main className={styles.content}>
          {termsAcceptanceRequired ? (
            <div className={styles.bannerRow}>
              <Banner
                variant="warning"
                title="Updated Terms & Conditions available."
                description={termsAcceptanceRequired.message}
                actions={
                  <Link
                    href={termsSettingsHref}
                    ariaLabel="Review and accept the latest terms and conditions"
                  >
                    Review terms
                  </Link>
                }
              />
            </div>
          ) : null}
          <ContextTabRouteContent>{children}</ContextTabRouteContent>
        </main>
      </div>
    </ChatRealtimeProvider>
  );

  if (
    !currentUser?.id ||
    !currentUserContextWorkspace?.id ||
    currentUserContextWorkspace.slug !== workspaceSlug
  ) {
    return shell;
  }

  return (
    <ContextTabsProvider
      key={`${currentUser.id}:${currentUserContextWorkspace.id}`}
      userId={currentUser.id}
      workspaceId={currentUserContextWorkspace.id}
      workspaceSlug={workspaceSlug}
    >
      {shell}
    </ContextTabsProvider>
  );
}
